import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, ChevronRight, Copy, Gift, Send } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { formatGroupedNumber } from 'lib/dateFormat';
import { useLearnerReferralLink, useLearnerReferrals, type LearnerReferralLink } from 'modules/learnerReferrals';

const som = (value: number) => `${formatGroupedNumber(value)} so‘m`;

export interface InviteFriendCardProps {
  /** Home shows a one-line card that opens the link in a dialog; the referrals page shows it inline. */
  compact?: boolean;
}

/** The learner's invite link with copy and Telegram share. Renders nothing while the program is off. */
export function InviteFriendCard({ compact = false }: InviteFriendCardProps) {
  const { data } = useLearnerReferralLink();
  if (!data?.enabled || !(data.referrerBonusSom > 0 || data.referredBonusSom > 0)) return null;
  return compact ? <CompactInvite data={data} /> : <InlineInvite data={data} />;
}

function BonusText({ data }: { data: LearnerReferralLink }) {
  const { t } = useTranslation();
  return (
    <>
      {data.referrerBonusSom > 0 && t('referral.body', { amount: som(data.referrerBonusSom) })}
      {data.referredBonusSom > 0 && ` ${t('referral.body_friend', { amount: som(data.referredBonusSom) })}`}
    </>
  );
}

function GiftBadge({ className = 'h-10 w-10' }: { className?: string }) {
  return (
    <span aria-hidden="true" className={`flex shrink-0 items-center justify-center rounded-[13px] bg-foreground text-primary dark:bg-primary dark:text-primary-foreground ${className}`}>
      <Gift className="h-5 w-5" />
    </span>
  );
}

function CompactInvite({ data }: { data: LearnerReferralLink }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const teaser = data.referrerBonusSom > 0
    ? t('referral.teaser', { amount: som(data.referrerBonusSom) })
    : t('referral.body_friend', { amount: som(data.referredBonusSom) });

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        className="group flex h-full w-full items-center gap-4 rounded-[20px] border border-border bg-card px-5 py-4 text-left transition-colors hover:border-foreground/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <GiftBadge className="h-11 w-11" />
        <span className="min-w-0 flex-1">
          <span className="block font-display text-[15.5px] font-extrabold tracking-[-0.02em] text-foreground">{t('referral.title')}</span>
          <span className="block truncate text-[12.5px] text-muted-foreground">{teaser}</span>
        </span>
        <ChevronRight aria-hidden="true" className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-[560px] gap-5 rounded-[28px] p-6 sm:p-8"
          // Keep focus off the link field so its text isn't shown pre-selected on open.
          onOpenAutoFocus={(e) => { e.preventDefault(); (e.currentTarget as HTMLElement).focus(); }}
        >
          <DialogHeader className="items-start gap-3 space-y-0 text-left">
            <GiftBadge />
            <div className="w-full">
              <DialogTitle className="font-display text-[26px] font-extrabold leading-[1.1] tracking-[-0.04em]">{t('referral.title')}</DialogTitle>
              <DialogDescription className="mt-1.5 leading-relaxed">
                <BonusText data={data} />
              </DialogDescription>
            </div>
          </DialogHeader>

          <LinkActions data={data} stacked />

          <ReferralStats />

          <ol className="divide-y divide-border text-sm text-foreground">
            {[
              t('referral.step_send'),
              t('referral.step_join'),
              data.referrerBonusSom > 0
                ? t('referral.step_bonus', { amount: som(data.referrerBonusSom) })
                : t('referral.step_plan'),
            ].map((step, i) => (
              <li key={step} className="flex items-start gap-3 py-2.5 first:pt-0">
                <span aria-hidden="true" className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-foreground text-xs font-extrabold tabular-nums text-primary dark:bg-primary dark:text-primary-foreground">
                  {i + 1}
                </span>
                <span className="leading-snug">{step}</span>
              </li>
            ))}
          </ol>

          <Link
            to="/portal/settings/referrals"
            onClick={() => setOpen(false)}
            className="justify-self-start rounded-sm text-[13px] font-medium text-foreground/80 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('referral.my_invites')}
          </Link>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Invited, joined and earned so far; hidden until the first load answers. */
function ReferralStats() {
  const { t } = useTranslation();
  const { data } = useLearnerReferrals();
  if (!data) return null;
  const stats = [
    { label: t('referral.stat_invited'), value: String(data.invited) },
    { label: t('referral.stat_rewarded'), value: String(data.rewarded) },
    { label: t('referral.stat_earned'), value: som(data.earnedSom) },
  ];
  return (
    <dl className="grid grid-cols-3 gap-2.5">
      {stats.map((stat) => (
        <div key={stat.label} className="rounded-2xl border border-border bg-muted/50 px-3.5 py-3">
          <dd className="font-display text-xl font-extrabold tracking-[-0.03em] text-foreground tabular-nums">{stat.value}</dd>
          <dt className="text-[12px] text-muted-foreground">{stat.label}</dt>
        </div>
      ))}
    </dl>
  );
}

function InlineInvite({ data }: { data: LearnerReferralLink }) {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="invite-friend-title" className="rounded-[20px] border border-border bg-card p-5 md:p-6">
      <div className="flex items-start gap-3.5">
        <GiftBadge />
        <div className="min-w-0 flex-1">
          <h2 id="invite-friend-title" className="text-[15px] font-semibold text-foreground">{t('referral.title')}</h2>
          <p className="mt-1 max-w-xl text-sm leading-relaxed text-muted-foreground"><BonusText data={data} /></p>
        </div>
      </div>
      <div className="mt-4">
        <LinkActions data={data} />
      </div>
    </section>
  );
}

/** Read-only link field with copy and Telegram share. */
function LinkActions({ data, stacked = false }: { data: LearnerReferralLink; stacked?: boolean }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(resetTimer.current), []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.link);
      setCopied(true);
      clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  const shareText = data.referredBonusSom > 0
    ? t('referral.share_text_bonus', { amount: som(data.referredBonusSom) })
    : t('referral.share_text');
  const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(data.link)}&text=${encodeURIComponent(shareText)}`;

  return (
    <div className={stacked ? 'flex flex-col gap-2' : 'flex flex-col gap-2 sm:flex-row sm:items-center'}>
      <label htmlFor="invite-friend-link" className="sr-only">{t('referral.link_label')}</label>
      <input
        id="invite-friend-link"
        readOnly
        value={data.link}
        onFocus={(e) => e.currentTarget.select()}
        className={`h-10 min-w-0 ${stacked ? '' : 'flex-1 '}rounded-md border border-border bg-background px-3 font-mono text-[13px] text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
      />
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={copy} className={stacked ? 'flex-1' : 'flex-1 sm:flex-none'} aria-live="polite">
          {copied ? <Check className="mr-1.5 h-4 w-4" /> : <Copy className="mr-1.5 h-4 w-4" />}
          {copied ? t('referral.copied') : t('referral.copy')}
        </Button>
        <Button asChild className={stacked ? 'flex-1' : 'flex-1 sm:flex-none'}>
          <a href={telegramUrl} target="_blank" rel="noopener noreferrer">
            <Send className="mr-1.5 h-4 w-4" />
            {t('referral.share_telegram')}
          </a>
        </Button>
      </div>
    </div>
  );
}

export default InviteFriendCard;
